import BpkImage, {
  withLazyLoading,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import Button from '../Button';
import Icon from '../common/Icon';

import type { SnippetEditorialProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/SnippetEditorialProps';

import STYLES from './SnippetEditorial.module.scss';

const isBrowser = typeof window !== 'undefined';
// Support for SSR
const documentIfExists = isBrowser ? document : null;
// withLazyLoading will not use document when rendered server side so it's safe
// pass null.
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);

export default function SnippetEditorial({
  body,
  buttons,
  image,
  subtitle,
  tags,
  title,
}: SnippetEditorialProps) {
  const hasTitles = title || subtitle;
  const hasTags = tags && tags.length > 0;
  const hasButtons = buttons && buttons.length > 0;
  const hasBody = hasTags || body || hasButtons;
  const hasContent = hasTitles || hasBody;

  return (
    <div
      data-testid="snippet-editorial"
      className={classNames([
        STYLES.SnippetEditorial,
        image && STYLES[`SnippetEditorial__${image.imagePosition}`],
      ])}
    >
      {image && (
        <div className={STYLES.ImageContainer}>
          <div className={STYLES.ImageMobile}>
            <LazyLoadedImage
              src={image.src}
              altText={image.altText}
              aspectRatio={1.715}
            />
          </div>
          <div className={STYLES.ImageDesktop}>
            <LazyLoadedImage
              src={image.src}
              altText={image.altText}
              aspectRatio={1.45}
            />
          </div>
        </div>
      )}
      {hasContent && (
        <div className={STYLES.ContentContainer}>
          {hasTitles && (
            <div className={STYLES.TitlesContainer}>
              {title && <h3 className={STYLES.Title}>{title}</h3>}
              {subtitle && (
                <BpkText tagName="p" textStyle={TEXT_STYLES.editorial3}>
                  {subtitle}
                </BpkText>
              )}
            </div>
          )}
          {image && image.imagePosition === 'top' && (
            <div className={STYLES.ImageContainer}>
              <LazyLoadedImage
                src={image.src}
                altText={image.altText}
                aspectRatio={2.025}
              />
            </div>
          )}
          {hasBody && (
            <div className={STYLES.BodyContainer}>
              {hasTags && (
                <div className={STYLES.TagsContainer}>
                  {tags.map((tag) => {
                    const IconComponent = Icon(tag.icon);

                    return (
                      <div
                        key={`${tag.icon}-${tag.text}`}
                        className={STYLES.Tag}
                      >
                        <IconComponent />
                        <BpkText tagName="span">{tag.text}</BpkText>
                      </div>
                    );
                  })}
                </div>
              )}
              {body && (
                <div className={STYLES.TextContainer}>
                  <BpkText tagName="p" textStyle={TEXT_STYLES.bodyLongform}>
                    {body}
                  </BpkText>
                </div>
              )}
              {hasButtons && (
                <div className={STYLES.ButtonsContainer}>
                  {/* TODO: QUAR-1784 - remove enableExperimentTracking prop once homepage_hero_redirect_offsite experiment is done */}
                  {buttons.map((buttonProps) => (
                    <Button
                      key={`${buttonProps.label}-${buttonProps.href}`}
                      {...buttonProps}
                      enableExperimentTracking={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
